import { HttpHeaders, HttpRequest } from '@angular/common/http';
import dayjs from 'dayjs';
import { version } from '../../../../environments/version';
import {
  ACCESS_TOKEN_KEY,
  LEGACY_API_KEY,
  REFRESH_TOKEN_KEY,
} from '../../../auth/services/auth.utils';
import { StorageContextService } from '../../../context/storage-context/storage-context.service';
import { CookieService } from '../../services/cookies/cookie.service';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';
import {
  isConfiguredAuthRequest,
  isConfiguredStretchitApiRequest,
  shouldAttachAccessToken,
  shouldAttachStretchitHeaders,
} from './request-classifier';

import { AppConfiguration } from '../../services/configuration/app.configuration';

type HeaderMap = Record<string, string>;

export type StretchitHeaderDeps = {
  cookies: CookieService;
  googleAnalytics: GoogleAnalyticsService;
  storage: StorageContextService;
  config: AppConfiguration;
};

export function buildStretchitHeaders(
  req: HttpRequest<unknown>,
  deps: StretchitHeaderDeps
): HttpHeaders {
  const headers: HeaderMap = {};
  const isAuthRequest = isConfiguredAuthRequest(req, deps.config);
  const accessToken = deps.storage.getItem(ACCESS_TOKEN_KEY);

  if (shouldAttachStretchitHeaders(req)) {
    const attributes = {
      _fbc: deps.storage.getItem('_fbc') || deps.cookies.get('_fbc') || undefined,
      _fbp: deps.storage.getItem('_fbp') || deps.cookies.get('_fbp') || undefined,
      _ga: deps.storage.getItem('_ga') || deps.cookies.get('_ga') || undefined,
      _gid: deps.storage.getItem('_gid') || deps.cookies.get('_gid') || undefined,
      _ttp: deps.storage.getItem('_ttp') || deps.cookies.get('_ttp') || undefined,
    };

    headers['x-app-version'] = `web:${version}`;
    headers['x-attribute'] = JSON.stringify(attributes);

    const timezone = dayjs.tz.guess();
    if (timezone) {
      headers['x-time-zone'] = timezone;
      headers['X-Time-Zone'] = timezone;
    }
  }

  const legacyApiKey = getLegacyApiKeyForXToken(deps.storage, accessToken);
  const xToken =
    legacyApiKey ||
    (isConfiguredStretchitApiRequest(req, deps.config)
      ? deps.storage.getItem(REFRESH_TOKEN_KEY)
      : '');

  if (!isAuthRequest && shouldAttachAccessToken(req, xToken)) {
    headers['x-token'] = `ApiKey="${xToken.replace(/\s/g, '+')}"`;
  }

  if (shouldAttachAccessToken(req, accessToken)) {
    if (isJwt(accessToken)) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    headers['X-Frame-Options'] = 'SAMEORIGIN';
  }

  if (shouldAttachStretchitHeaders(req)) {
    for (const name of ['x-ad-id', 'x-apps-flyer-uid']) {
      const value = deps.storage.getItem(name);
      if (value) headers[name] = value;
    }

    const clientId = deps.googleAnalytics.getClientId();
    if (clientId) {
      headers['x-ga-client-id'] = clientId;
    }

    const sessionId = deps.googleAnalytics.getSessionGtagId();
    if (sessionId) {
      headers['ga-session'] = sessionId;
    }
  }

  let result = req.headers;
  if (isAuthRequest) result = result.delete('x-token');
  for (const [name, value] of Object.entries(headers)) {
    result = result.set(name, value);
  }
  return result;
}

function getLegacyApiKeyForXToken(
  storage: StorageContextService,
  accessToken: string
): string | null {
  const legacyApiKey = storage.getItem(LEGACY_API_KEY);

  if (legacyApiKey) {
    return legacyApiKey;
  }

  return accessToken && !isJwt(accessToken) ? accessToken : null;
}

function isJwt(token: string): boolean {
  return token.split('.').length === 3;
}
